import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues, FormikErrors } from "formik";
import { bindActionCreators, Dispatch } from "redux";

import { Form, PickerInput, DataColumnDef, ITextQuery, IDataColumnDefProps, Checkbox, ApplyButton, OkButton, ValidationMessage, CancelLink } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers";
import styles from "ui/screens/translation-managers/message-details/MessageDetails.module.scss";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import MessageRelationshipsGrid from "../grid/MessageRelationshipsGrid";
import { messageRelationshipsActions } from "../MessageRelationships.actions";
import { relationshipExists } from "../MessageRelationships.helpers";
import { IMessageRelationshipsState, IMessage } from "../MessageRelationships.state";

export interface IMessageRelationshipsFormProps {
  loadPickerMessages: (criterion: IPickerSearchCriterion<IMessage>) => void;
  selectNewRelationship: (message: IMessage[]) => void;
  addRelationship: (payload: { messageId: string; newRelationship: IMessage; version: number; redirect: boolean }) => void;
  cancelLinkClick: () => void;
  setValidationBanner: () => void;
  clearValidationNotification: () => void;
  messageDetails: IMessage;
  messagesPickerData: IMessage[];
  relationships: IMessage[];
  isParentMessageId?: boolean;
  newRelationship: IMessage[];
  notifications: INotification[];
}

const MessageRelationshipsForm: React.FC<IMessageRelationshipsFormProps> = ({
  loadPickerMessages,
  selectNewRelationship,
  addRelationship,
  cancelLinkClick,
  setValidationBanner,
  clearValidationNotification,
  messageDetails,
  messagesPickerData,
  relationships,
  isParentMessageId,
  newRelationship,
  notifications
}) => {
  const localizedValues = useLocalizedValues(() => ({
    manageRelationshipsTitle: displayMessages.heading("manageRelationships"),
    messageIdTitle: displayMessages.property("messageId"),
    textTitle: displayMessages.property("text"),
    relationshipsSectionTitle: displayMessages.heading("relationships"),
    manageRelationshipsLabel: displayMessages.property("manageRelationships"),
    parentCheckboxLabel: displayMessages.property("parent"),
    relationshipExistsMessage: displayMessages.error("relationshipExists")
  }));

  const localizer = useLocalizer();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexTechnicalWriter;

  const [redirectFlag, setRedirectFlag] = useState<boolean>(false);

  const onNewRelationshipSelected = useCallback(
    async (rows: IMessage[], validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>, setValues: (values: FormikValues) => void) => {
      selectNewRelationship(rows);
      setValues({
        newRelationship: rows
      });
      await validateForm();
    },
    [selectNewRelationship]
  );

  const onValidate = useCallback(
    (values: FormikValues) => {
      const errors: any = {};
      if (values.newRelationship.length === 0) {
        errors.manageRelationshipsPicker = localizer(validationMessages.error("propertyIsRequired"), {
          property: localizedValues.manageRelationshipsLabel
        });

        setValidationBanner();
      } else if (relationshipExists(values.newRelationship[0].messageId, relationships)) {
        errors.manageRelationshipsPicker = localizedValues.relationshipExistsMessage;
        setValidationBanner();
      } else {
        clearValidationNotification();
      }

      return errors;
    },
    [relationships, localizedValues.manageRelationshipsLabel, localizedValues.relationshipExistsMessage, localizer, clearValidationNotification, setValidationBanner]
  );

  const onSubmitButtonClick = useCallback((handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void, redirect: boolean) => {
    handleSubmit();
    setRedirectFlag(redirect);
  }, []);

  const renderPicker = (
    errors: FormikErrors<FormikValues>,
    handleBlur: (e: React.FocusEvent<any>) => void,
    validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>,
    setValues: (values: FormikValues) => void
  ) => {
    return (
      <PickerInput
        name="manageRelationshipsPicker"
        className={errors.manageRelationshipsPicker && styles.invalidField}
        dialogTitle={localizedValues.manageRelationshipsTitle}
        keySelector={(row: IMessage) => row.messageId}
        displaySelector={(row: IMessage) => row.messageId}
        selected={newRelationship}
        data={messagesPickerData}
        onBlur={handleBlur}
        disabled={isReadOnlyMode}
        onSelectionChanged={rows => onNewRelationshipSelected(rows as IMessage[], validateForm, setValues)}
        onSearch={(query: ITextQuery, columns: Array<IDataColumnDefProps<IMessage>>) => loadPickerMessages({ query: query, searchedColumns: columns })}
      >
        <DataColumnDef<IMessage> key="1" id="messageId" searchable={true} sortable={true} title={localizedValues.messageIdTitle} valueSelector={row => row.messageId}>
          {(row: IMessage) => row.messageId}
        </DataColumnDef>
        <DataColumnDef<IMessage> key="2" id="text" searchable={true} sortable={true} title={localizedValues.textTitle} valueSelector={row => row.text}>
          {(row: IMessage) => row.text}
        </DataColumnDef>
      </PickerInput>
    );
  };

  const renderRelationshipsSection = () => {
    return (
      <Form.Section title={localizedValues.relationshipsSectionTitle}>
        <Form.Row>
          <MessageRelationshipsGrid />
        </Form.Row>
      </Form.Section>
    );
  };

  return (
    <Formik
      initialValues={{
        newRelationship: []
      }}
      validate={onValidate}
      onSubmit={(_: FormikValues, { setSubmitting }) => {
        setSubmitting(false);
        addRelationship({
          messageId: messageDetails.messageId,
          newRelationship: newRelationship[0],
          version: messageDetails.version,
          redirect: redirectFlag
        });
      }}
    >
      {({ handleSubmit, handleBlur, errors, setValues, validateForm }) => (
        <Form className={styles.messageFormTab}>
          <Form.Header>
            <Banner messages={notifications} />
          </Form.Header>
          <Form.Body className={styles.formBody}>
            <Form.Section title={localizedValues.manageRelationshipsLabel}>
              <Form.Row>
                <Form.FieldPair labelText={localizedValues.parentCheckboxLabel}>
                  <Checkbox disabled readOnly checked={isParentMessageId} />
                </Form.FieldPair>
              </Form.Row>
              <Form.Row id="manage-relationships-picker">
                <Form.FieldPair required labelText={localizedValues.manageRelationshipsTitle}>
                  {renderPicker(errors, handleBlur, validateForm, setValues)}
                  <ValidationMessage message={errors.manageRelationshipsPicker ? (errors.manageRelationshipsPicker as string) : ""} />
                </Form.FieldPair>
              </Form.Row>
            </Form.Section>
            {relationships.length > 0 && renderRelationshipsSection()}
          </Form.Body>
          <Form.Footer className={styles.formFooter}>
            <CancelLink onClick={cancelLinkClick} />
            {roles.PlexTechnicalWriter && (
              <>
                <ApplyButton disabled={!messageDetails.isParentMessage && relationships.length > 0} onClick={() => onSubmitButtonClick(handleSubmit, false)} />
                <OkButton disabled={!messageDetails.isParentMessage && relationships.length > 0} onClick={() => onSubmitButtonClick(handleSubmit, true)} />
              </>
            )}
          </Form.Footer>
        </Form>
      )}
    </Formik>
  );
};

export default connect(
  (state: { messageRelationships: IMessageRelationshipsState }) => {
    return {
      newRelationship: state.messageRelationships.newRelationship,
      messagesPickerData: state.messageRelationships.messagesPickerData,
      messageDetails: state.messageRelationships.messageDetails,
      notifications: state.messageRelationships.notifications
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectNewRelationship: messageRelationshipsActions.selectNewRelationship,
        loadPickerMessages: messageRelationshipsActions.loadPickerMessages,
        addRelationship: messageRelationshipsActions.addRelationship,
        cancelLinkClick: messageRelationshipsActions.cancelLinkClick,
        setValidationBanner: messageRelationshipsActions.setValidationBanner,
        clearValidationNotification: messageRelationshipsActions.clearValidationNotification
      },
      dispatch
    )
)(MessageRelationshipsForm);
